import type { AlertType, ApiKeyScope, AssetCondition, PositionSource, QualificationStatus, Role, ZoneKind } from '../api/types';

/** French labels and UI rules derived from domain values. */

export const ROLE_LABELS: Record<Role, string> = {
  platform_admin: 'Administrateur plateforme',
  tenant_admin: 'Administrateur',
  operator: 'Opérateur',
  viewer: 'Lecture seule',
};

export const ROLE_DESCRIPTIONS: Record<Exclude<Role, 'platform_admin'>, string> = {
  tenant_admin: 'Tout gérer, y compris les utilisateurs et les clés API',
  operator: 'Modifier les plans, les zones, les ancres et les matériels',
  viewer: 'Consulter uniquement',
};

export const ZONE_KIND_LABELS: Record<ZoneKind, string> = {
  workstation: 'Poste de travail',
  storage: 'Stockage',
  corridor: 'Circulation',
  cleanroom: 'Zone à atmosphère contrôlée',
  washing: 'Laverie',
  quarantine: 'Quarantaine',
  other: 'Autre',
};

export const QUALIFICATION_LABELS: Record<QualificationStatus, string> = {
  qualified: 'Qualifié',
  requalification_required: 'À requalifier',
  out_of_service: 'Hors service',
  not_applicable: 'Non applicable',
};

export const ALERT_LABELS: Record<AlertType, string> = {
  asset_lost: 'Matériel perdu',
  battery_low: 'Batterie faible',
  calibration_overdue: 'Étalonnage dépassé',
  calibration_due_soon: 'Étalonnage à renouveler',
  dwell_exceeded: 'Temps de présence dépassé',
  condition_reported: 'État signalé',
};

export const CONDITION_LABELS: Record<AssetCondition, { label: string; tone: 'green' | 'red' | 'yellow' | 'neutral'; action: string }> = {
  ok: { label: 'En service', tone: 'green', action: 'Remettre en service' },
  problem: { label: 'Problème signalé', tone: 'red', action: 'Signaler un problème' },
  maintenance: { label: 'Maintenance à faire', tone: 'yellow', action: 'Demander une maintenance' },
  out_of_service: { label: 'Hors service', tone: 'neutral', action: 'Mettre hors service' },
};

/** How a position was obtained, in short and long forms. */
export const SOURCE_LABELS: Record<PositionSource, { short: string; long: string }> = {
  ble: { short: 'BLE', long: 'Tag BLE, position précise' },
  rfid: { short: 'RFID, zone', long: 'Lecteur RFID, précision zone' },
  manual: { short: 'placé à la main', long: 'Placé à la main' },
  shelf: { short: 'étagère', long: 'Rangé sur étagère' },
};

export const SCOPE_LABELS: Record<ApiKeyScope, string> = {
  'positions:write': 'Envoyer des positions',
  'data:read': 'Lire les données',
  'labels:sync': 'Étiquettes électroniques',
};

export { ASSET_TYPES } from './assetTypes';

export const ZONE_COLORS = ['#2557D6', '#23917F', '#D99A00', '#7B61FF', '#C9474D', '#2F8F5B', '#E0662F', '#5B6B7A'];

export type Freshness = 'live' | 'stale' | 'lost' | 'never';

/** Signal freshness, consistent with the server's « lost after 5 minutes » rule. */
export function freshness(recordedAt: string | null | undefined, now = Date.now()): Freshness {
  if (!recordedAt) return 'never';
  const age = (now - Date.parse(recordedAt)) / 1000;
  return age < 20 ? 'live' : age < 300 ? 'stale' : 'lost';
}

export const FRESHNESS: Record<Freshness, { tone: 'green' | 'yellow' | 'red' | 'neutral'; label: string }> = {
  live: { tone: 'green', label: 'En ligne' },
  stale: { tone: 'yellow', label: 'Signal faible' },
  lost: { tone: 'red', label: 'Perdu' },
  never: { tone: 'neutral', label: 'Jamais vu' },
};

export type CalibrationTone = { tone: 'green' | 'yellow' | 'red'; label: string };

export function calibrationTone(due: string | null): CalibrationTone | null {
  if (!due) return null;
  const days = Math.ceil((Date.parse(`${due}T23:59:59`) - Date.now()) / 86_400_000) - 1;
  if (days < 0) return { tone: 'red', label: `Dépassé de ${-days} j` };
  if (days <= 30) return { tone: 'yellow', label: `Échéance dans ${days} j` };
  return { tone: 'green', label: 'Valide' };
}

export const can = {
  manageUsers: (role: Role) => role === 'platform_admin' || role === 'tenant_admin',
  editLayout: (role: Role) => role !== 'viewer',
  editAssets: (role: Role) => role !== 'viewer',
};
